import {
  FormContext,
  FormContextProvider,
  FormSchema,
  FormSchemaFields,
  ToggleSize,
} from '@sw-sw/lib-form';
import React, { EventHandler, useContext, useEffect, useState } from 'react';
import FormModal from '../form/modal/FormModal';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import permitApi from '../../../utils/api/permit';

export interface ITrackPermitModalProps {
  onSubmit: EventHandler<any>;
  onCancel: EventHandler<any>;
  fileId?: number;
  show?: Boolean;
}

const schema: FormSchema = {
  permit_number: {
    label: 'Permit Number*',
    controlType: UIControlType.text,
    validation: {
      required: true,
    },
  },
  permit_type: {
    label: 'Permit Type*',
    controlType: UIControlType.select,
    options: [
      {
        label: 'Construction General Permit',
        value: 'Construction General Permit',
      },
      { label: 'Dewatering Permit', value: 'Dewatering Permit' },
      { label: 'Air Quality', value: 'Air Quality' },
      { label: 'MS4 / Local Permit', value: 'MS4 / Local Permit' },
      {
        label: 'Land User / Site Activity Permit',
        value: 'Land User / Site Activity Permit',
      },
      { label: 'Common Plan Permit', value: 'Common Plan Permit' },
      { label: 'Other', value: 'Other' },
    ],
    validation: {
      required: true,
    },
  },
  type_name: {
    label: 'Custom Entry*',
    controlType: UIControlType.text,
    validation: {
      required: false,
    },
  },
  issued_date: {
    label: 'Issued Date*',
    controlType: UIControlType.date,
    validation: {
      required: true,
    },
  },
  expiration_date: {
    label: 'Expiration Date',
    controlType: UIControlType.date,
    validation: {
      minDate: new Date(),
    },
  },
  status: {
    label: 'Active/Inactive*',
    controlType: UIControlType.toggleButton,
    validation: {
      required: true,
    },
    toggleSize: 'sm' as ToggleSize,
  },
};

const TrackPermitModalUI: React.FC<ITrackPermitModalProps> = ({
  onSubmit,
  onCancel,
  show = false,
}: ITrackPermitModalProps) => {
  const formContext = useContext(FormContext);
  const [formSchema, setFormSchema] = useState(schema);

  useEffect(() => {
    const isOther = formContext.value.permit_type === 'Other';

    setFormSchema((prevSchema) => ({
      ...prevSchema,
      type_name: {
        ...prevSchema.type_name,
        validation: {
          ...prevSchema.type_name?.validation,
          required: isOther,
        },
        style: {
          ...prevSchema.type_name?.style,
          display: isOther ? 'block' : 'none',
        },
      },
    }));

    if (!isOther) {
      formContext.set('type_name', '');
    }
  }, [formContext.value.permit_type]);

  return (
    <React.Fragment>
      {show && (
        <FormModal
          onSubmit={async () => onSubmit(formContext.value)}
          onCancel={onCancel}
          modalProps={{
            title: 'Enter Permit Information',
          }}
        >
          <FormSchemaFields
            schema={formSchema}
            onChange={formContext.set}
            formData={formContext.value}
          />
        </FormModal>
      )}
    </React.Fragment>
  );
};

const TrackPermitModal: React.FC<ITrackPermitModalProps> = ({
  onSubmit,
  onCancel,
  fileId,
  show,
}) => {
  const initialFormData = {
    permit_number: '',
    permit_type: '',
    type_name: '',
    issued_date: '',
    expiration_date: '',
    status: false,
    id: null,
  };
  const [formData, setFormData] = useState(initialFormData);
  useEffect(() => {
    if (show && fileId) {
      permitApi.getByFileId(fileId).then((response) => {
        if (response) {
          setFormData({
            ...response,
          });
        } else {
          setFormData(initialFormData);
        }
      });
    }
  }, [show, fileId]);

  const handleSubmit = async (data: any) => {
    const permitData = {
      ...data,
      project_upload_id: fileId,
    };

    // Handle empty expiration_date by setting it to null
    if (permitData.expiration_date === '' || !permitData.expiration_date) {
      permitData.expiration_date = null;
    }

    if (permitData.id) {
      await permitApi.update(permitData, permitData.id);
    } else {
      await permitApi.create(permitData);
    }

    onSubmit(permitData);
  };

  return (
    <FormContextProvider initialValue={formData}>
      <TrackPermitModalUI
        onSubmit={handleSubmit}
        onCancel={onCancel}
        show={show}
      />
    </FormContextProvider>
  );
};

export default TrackPermitModal;
